<template>
  <nav class="tm-nav">
    <div class="tm-nav__in">
      <a class="tm-nav__logo" href="/">topment</a>

      <!-- Signed out: marketing nav. Signed in: app nav. -->
      <div class="tm-nav__links" :class="{ open: mobileOpen }">
        <template v-if="signedIn">
          <a class="tm-nav__link" :class="{ on: isOn('dashboard-cv') }" @click="go('dashboard-cv')">My CV</a>
          <a class="tm-nav__link" :class="{ on: isOn('dashboard-careers') }" @click="go('dashboard-careers')">Careers</a>
          <a class="tm-nav__link" :class="{ on: isOn('dashboard-standardization') }" @click="go('dashboard-standardization')">Standardization</a>
        </template>
        <!-- Signed out there is nothing worth putting here: the standardization
             tools live in the footer, and the only action that matters is the CTA. -->

        <!-- account actions collapse into the mobile sheet -->
        <div class="tm-nav__mobile-actions">
          <template v-if="signedIn">
            <a class="tm-nav__link" @click="go('dashboard-profile')">Profile</a>
            <a class="tm-nav__link" @click="logout()">Sign out</a>
          </template>
          <template v-else>
            <a class="tm-nav__link" @click="go('signin')">Sign in</a>
            <a class="tm-nav__link" @click="go('dashboard-cv')">Upload your CV</a>
          </template>
        </div>
      </div>

      <div class="tm-nav__right">
        <template v-if="signedIn">
          <div class="tm-nav__account">
            <button class="tm-nav__avatar-btn" @click="menuOpen = !menuOpen" :aria-expanded="menuOpen"
                    aria-haspopup="true" aria-label="Account menu">
              <img v-if="previewImage" :src="previewImage" class="tm-nav__avatar" alt="">
              <span v-else class="tm-nav__avatar tm-nav__avatar--initials">{{ initials }}</span>
            </button>
            <div v-if="menuOpen" class="tm-nav__menu">
              <div class="tm-nav__menu-name">{{ name }}</div>
              <button class="tm-nav__menu-item" @click="go('dashboard-careers')">My dashboard</button>
              <button class="tm-nav__menu-item" @click="go('dashboard-cv')">My CV</button>
              <button class="tm-nav__menu-item" @click="go('dashboard-profile')">Profile</button>
              <div class="tm-nav__menu-sep"></div>
              <button class="tm-nav__menu-item" @click="logout()">Sign out</button>
            </div>
          </div>
        </template>
        <template v-else>
          <a class="tm-nav__signin" @click="go('signin')">Sign in</a>
          <button class="tm-btn-primary tm-nav__cta" @click="go('dashboard-cv')">Upload your CV</button>
        </template>

        <button class="tm-nav__burger" @click="mobileOpen = !mobileOpen"
                :aria-expanded="mobileOpen" aria-label="Toggle navigation">
          <span></span><span></span><span></span>
        </button>
      </div>
    </div>
  </nav>
</template>

<script>
import { AuthApiService } from '@/services/AuthApiService';
import { computed, defineComponent, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { profile } from '../services/Store';

export default defineComponent({
  setup() {
    const router = useRouter()
    const signedIn = ref(false)
    const name = ref('')
    const previewImage = ref()
    const menuOpen = ref(false)
    const mobileOpen = ref(false)

    const initials = computed(() => (name.value || '')
      .split(' ').filter(Boolean).slice(0, 2)
      .map(w => w[0].toUpperCase()).join('') || '?')

    function isOn(routeName) {
      return router.currentRoute.value.name === routeName
    }

    function getprofile() {
      if (!localStorage.getItem('user_id')) {
        signedIn.value = false
        return
      }
      name.value = localStorage.getItem('user_name') || ''
      signedIn.value = true
      AuthApiService.getProfile(localStorage.getItem('user_id')).then((res) => {
        if (res.status == 200) previewImage.value = res.data.url
      }).catch(() => { return })
    }

    watch(profile, () => {
      if (profile.count == 1) { getprofile(); profile.count = 0 }
    })

    // Auth state can change on any navigation (login, logout, 401 redirect).
    watch(() => router.currentRoute.value.name, () => {
      menuOpen.value = false
      mobileOpen.value = false
      getprofile()
    })

    function closeMenus(e) {
      if (!e.target.closest('.tm-nav__account')) menuOpen.value = false
    }

    onMounted(() => {
      getprofile()
      document.addEventListener('click', closeMenus)
    })
    onUnmounted(() => document.removeEventListener('click', closeMenus))

    // Routes that need an id param, and the auth gate for app routes.
    function go(value) {
      menuOpen.value = false
      mobileOpen.value = false
      const uid = localStorage.getItem('user_id')

      if (value === 'signin') return router.push({ name: 'signin' })
      if (!uid) return router.push({ name: 'signin' })

      if (value === 'dashboard-profile' || value === 'dashboard-standardization') {
        return router.push({ name: value, params: { id: uid } })
      }
      router.push({ name: value })
    }

    function logout() {
      localStorage.removeItem('user_id')
      localStorage.removeItem('user_name')
      signedIn.value = false
      menuOpen.value = false
      mobileOpen.value = false
      router.push({ name: 'signin' })
    }

    return { router, signedIn, name, initials, previewImage, menuOpen, mobileOpen, isOn, go, logout }
  },
})
</script>

<style scoped>
.tm-nav {
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(255, 255, 255, 0.86);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--tm-border);
}

.tm-nav__in {
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 24px;
  height: 60px;
  display: flex;
  align-items: center;
  gap: 28px;
}

.tm-nav__logo {
  font-family: var(--tm-font-display);
  font-weight: 800;
  font-size: 20px;
  letter-spacing: -0.03em;
  color: var(--tm-ink);
  text-decoration: none;
  flex: none;
}

.tm-nav__links { display: flex; gap: 3px; }

.tm-nav__link {
  font-size: 13.5px;
  font-weight: 500;
  color: var(--tm-ink-muted);
  text-decoration: none;
  padding: 7px 12px;
  border-radius: var(--tm-radius-sm);
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.16s var(--tm-ease), color 0.16s var(--tm-ease);
}

.tm-nav__link:hover { background: var(--tm-surface-sunk); color: var(--tm-ink); }
.tm-nav__link.on { color: var(--tm-ink); background: var(--tm-surface-sunk); font-weight: 600; }

.tm-nav__right { margin-left: auto; display: flex; align-items: center; gap: 12px; }

.tm-nav__signin {
  font-size: 13.5px;
  font-weight: 500;
  color: var(--tm-ink-muted);
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
}
.tm-nav__signin:hover { color: var(--tm-ink); }

.tm-nav__cta { padding: 8px 16px; font-size: 13.5px; }

/* account */
.tm-nav__account { position: relative; }

.tm-nav__avatar-btn {
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: block;
}

.tm-nav__avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  display: grid;
  place-items: center;
  border: 1px solid var(--tm-border);
}

.tm-nav__avatar--initials {
  background: linear-gradient(140deg, var(--tm-accent), #21A0C4);
  color: #fff;
  font-family: var(--tm-font-mono);
  font-size: 11.5px;
  font-weight: 700;
  border-color: transparent;
}

.tm-nav__menu {
  position: absolute;
  right: 0;
  top: calc(100% + 10px);
  min-width: 190px;
  background: var(--tm-surface);
  border: 1px solid var(--tm-border);
  border-radius: var(--tm-radius-md);
  box-shadow: var(--tm-shadow-lg);
  padding: 6px;
  z-index: 10;
}

.tm-nav__menu-name {
  font-size: 12px;
  color: var(--tm-ink-faint);
  padding: 8px 12px 6px;
  border-bottom: 1px solid var(--tm-border-soft);
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tm-nav__menu-item {
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  font-family: var(--tm-font-sans);
  font-size: 13.5px;
  color: var(--tm-ink-soft);
  padding: 8px 12px;
  border-radius: var(--tm-radius-xs);
  cursor: pointer;
}

.tm-nav__menu-item:hover { background: var(--tm-surface-sunk); color: var(--tm-ink); }

.tm-nav__menu-sep {
  height: 1px;
  background: var(--tm-border-soft);
  margin: 4px 0;
}

/* mobile */
.tm-nav__burger {
  display: none;
  flex-direction: column;
  gap: 4px;
  border: none;
  background: transparent;
  padding: 8px 4px;
  cursor: pointer;
}
.tm-nav__burger span {
  width: 18px;
  height: 2px;
  border-radius: 2px;
  background: var(--tm-ink);
}

.tm-nav__mobile-actions { display: none; }

@media (max-width: 820px) {
  .tm-nav__burger { display: flex; }
  .tm-nav__cta, .tm-nav__signin { display: none; }

  .tm-nav__links {
    position: absolute;
    top: 60px;
    left: 0;
    right: 0;
    flex-direction: column;
    gap: 2px;
    background: var(--tm-surface);
    border-bottom: 1px solid var(--tm-border);
    box-shadow: var(--tm-shadow-md);
    padding: 10px 16px 16px;
    display: none;
  }
  .tm-nav__links.open { display: flex; }

  .tm-nav__link { padding: 11px 12px; font-size: 15px; }

  .tm-nav__mobile-actions {
    display: flex;
    flex-direction: column;
    gap: 2px;
    border-top: 1px solid var(--tm-border-soft);
    margin-top: 8px;
    padding-top: 8px;
  }
}
</style>
